import React, { createContext, ReactNode, useCallback, useContext, useState } from 'react'
import { ModalError } from '~/components/shared/modalError'
import { ModalAlertDevelopment } from '~/components/shared/ModalAlertDevelopment'
import { IMessageCenterProps, IErrorModalProps, IAlertDevelopmentModalProps } from '~/interfaces'
import { message } from '~/services'
import { ModalCustomAlert } from '~/components/shared/ModalCustomAlert'

const defaultValue = {
  showModal: false,
  showAlertDevelopmentModal: false,
  showCustomAlertModal: false
}

const defaultInfoErrorModal = {
  title: message('messages.titleError'),
  subtitle: message('messages.paragraphError'),
  buttonMessage: message('messages.buttonError')
}

const defaultInfoAlertDevelopmentModal = {
  title: message('messages.underDevelopment'),
  subtitle: message('messages.thisFeatureWillBeDevelopedSoon'),
  buttonMessage: message('messages.close')
}

const MessageCenterContext = createContext<IMessageCenterProps>({ ...defaultValue } as IMessageCenterProps)

const MessageCenterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [showErrorModal, setErrorModal] = useState<boolean>(defaultValue.showModal)
  const [showAlertDevelopmentModal, setAlertDevelopmentModal] = useState<boolean>(
    defaultValue.showAlertDevelopmentModal
  )
  const [showCustomAlertModal, setCustomAlertModal] = useState<boolean>(defaultValue.showCustomAlertModal)
  const [modalInfoError, setModalInfoError] = useState<IErrorModalProps>(defaultInfoErrorModal)
  const [modalInfoAlertDevelopment, setModalInfoAlertDevelopment] = useState<IAlertDevelopmentModalProps>(
    defaultInfoAlertDevelopmentModal
  )

  const throwErrorModal = useCallback((props?: IErrorModalProps) => {
    const { title, subtitle, buttonMessage } = { ...props }
    const {
      title: defaultTitle,
      subtitle: defaultSubTitle,
      buttonMessage: defaultButtonMessage
    } = defaultInfoErrorModal

    setModalInfoError({
      title: title ?? defaultTitle,
      subtitle: subtitle ?? defaultSubTitle,
      buttonMessage: buttonMessage ?? defaultButtonMessage
    })

    setErrorModal(true)
  }, [])

  const throwAlertDevelopmentModal = useCallback((props?: IAlertDevelopmentModalProps) => {
    const { title, subtitle, buttonMessage } = { ...props }
    const {
      title: defaultTitle,
      subtitle: defaultSubTitle,
      buttonMessage: defaultButtonMessage
    } = defaultInfoAlertDevelopmentModal

    setModalInfoAlertDevelopment({
      title: title ?? defaultTitle,
      subtitle: subtitle ?? defaultSubTitle,
      buttonMessage: buttonMessage ?? defaultButtonMessage
    })

    setAlertDevelopmentModal(true)
  }, [])

  const throwCustomAlert = useCallback((props: IAlertDevelopmentModalProps) => {
    const { title, subtitle, buttonMessage } = { ...props }

    setModalInfoAlertDevelopment({
      title: title,
      subtitle: subtitle,
      buttonMessage: buttonMessage
    })

    setCustomAlertModal(true)
  }, [])

  return (
    <MessageCenterContext.Provider
      value={{
        showAlertDevelopmentModal,
        showCustomAlertModal,
        showModal: showErrorModal,
        throwAlertDevelopmentModal,
        throwErrorModal,
        throwCustomAlert
      }}
    >
      {children}
      <ModalError {...{ showErrorModal, modalInfo: modalInfoError }} onClose={() => setErrorModal(false)} />
      <ModalAlertDevelopment
        {...{ showAlertDevelopmentModal, modalInfo: modalInfoAlertDevelopment }}
        onClose={() => setAlertDevelopmentModal(false)}
      />
      <ModalCustomAlert
        {...{ showCustomAlertModal, modalInfo: modalInfoAlertDevelopment }}
        onClose={() => setCustomAlertModal(false)}
      />
    </MessageCenterContext.Provider>
  )
}

export const useMessageCenter = (): IMessageCenterProps => {
  return useContext(MessageCenterContext)
}

export { MessageCenterProvider, MessageCenterContext }
